import React, { useState } from 'react';
import { Header, Footer, MobileBottomNav } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Shield, Upload, CheckCircle2, AlertCircle, User, FileText, Camera } from 'lucide-react';

const KYCPage = () => {
  const [step, setStep] = useState(1);
  const [idDoc, setIdDoc] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [proofOfAddress, setProofOfAddress] = useState(null);
  const [idType, setIdType] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleFileUpload = (setter) => (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File must be under 10MB');
        return;
      }
      setter({ name: file.name, size: file.size, preview: URL.createObjectURL(file) });
      toast.success(`${file.name} uploaded`);
    }
  };

  const handleSubmit = () => {
    if (!idDoc || !selfie) {
      toast.error('Please upload required documents');
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setStep(3);
      toast.success('KYC documents submitted for review!');
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col pb-20 md:pb-0 overflow-x-hidden">
      <Header />
      <main className="flex-1 max-w-2xl mx-auto px-4 py-8 w-full">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="h-8 w-8 text-[#D4AF37]" />
          <div>
            <h1 className="text-3xl font-black uppercase text-white" style={{ fontFamily: 'Outfit' }}>
              KYC <span className="text-[#D4AF37]">Verification</span>
            </h1>
            <p className="text-gray-400">Complete your identity verification to unlock all features</p>
          </div>
        </div>

        {/* Progress */}
        <div className="flex gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-2 flex-1 rounded-full ${s <= step ? 'bg-[#D4AF37]' : 'bg-[#2D2D2D]'}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="card-dark space-y-6">
            <h2 className="font-bold text-lg text-white">Step 1: Identity Document</h2>
            <p className="text-gray-400 text-sm">Upload a clear photo of your government-issued ID document.</p>

            <div>
              <Label className="font-semibold text-white text-sm">Document Type</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {['Passport', 'Driver License', 'National ID'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setIdType(type)}
                    className={`p-3 rounded-lg border-2 text-center text-sm font-semibold ${
                      idType === type ? 'bg-[#D4AF37] text-[#1A1A1A] border-[#D4AF37]' : 'bg-[#2D2D2D] text-white border-[#3D3D3D]'
                    }`}
                    data-testid={`id-type-${type.toLowerCase().replace(' ', '-')}`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="font-semibold text-white text-sm">Upload Document</Label>
              <label className="mt-2 flex flex-col items-center justify-center border-2 border-dashed border-[#3D3D3D] rounded-lg p-8 cursor-pointer hover:border-[#D4AF37] transition-colors" data-testid="upload-id-doc">
                {idDoc ? (
                  <div className="text-center">
                    <CheckCircle2 className="h-10 w-10 text-[#28A745] mx-auto mb-2" />
                    <span className="text-white font-semibold text-sm">{idDoc.name}</span>
                  </div>
                ) : (
                  <>
                    <FileText className="h-10 w-10 text-gray-500 mb-2" />
                    <span className="text-gray-400 text-sm">Click to upload ID document</span>
                    <span className="text-xs text-gray-600 mt-1">JPG, PNG or PDF (max 10MB)</span>
                  </>
                )}
                <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleFileUpload(setIdDoc)} />
              </label>
            </div>

            <Button onClick={() => { if (!idType || !idDoc) { toast.error('Please select document type and upload'); return; } setStep(2); }} className="btn-primary w-full" data-testid="kyc-next-step1">
              Continue
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="card-dark space-y-6">
            <h2 className="font-bold text-lg text-white">Step 2: Selfie & Proof of Address</h2>

            <div>
              <Label className="font-semibold text-white text-sm">Face Photo (Selfie)</Label>
              <label className="mt-2 flex flex-col items-center justify-center border-2 border-dashed border-[#3D3D3D] rounded-lg p-8 cursor-pointer hover:border-[#D4AF37] transition-colors" data-testid="upload-selfie">
                {selfie ? (
                  <div className="text-center">
                    <CheckCircle2 className="h-10 w-10 text-[#28A745] mx-auto mb-2" />
                    <span className="text-white font-semibold text-sm">{selfie.name}</span>
                  </div>
                ) : (
                  <>
                    <Camera className="h-10 w-10 text-gray-500 mb-2" />
                    <span className="text-gray-400 text-sm">Upload a clear selfie</span>
                    <span className="text-xs text-gray-600 mt-1">Face must be clearly visible</span>
                  </>
                )}
                <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload(setSelfie)} />
              </label>
            </div>

            <div>
              <Label className="font-semibold text-white text-sm">Proof of Address (Optional)</Label>
              <label className="mt-2 flex flex-col items-center justify-center border-2 border-dashed border-[#3D3D3D] rounded-lg p-6 cursor-pointer hover:border-[#D4AF37] transition-colors" data-testid="upload-proof-address">
                {proofOfAddress ? (
                  <div className="text-center">
                    <CheckCircle2 className="h-8 w-8 text-[#28A745] mx-auto mb-1" />
                    <span className="text-white font-semibold text-sm">{proofOfAddress.name}</span>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-gray-500 mb-1" />
                    <span className="text-gray-400 text-sm">Utility bill or bank statement</span>
                  </>
                )}
                <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleFileUpload(setProofOfAddress)} />
              </label>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(1)} className="btn-secondary flex-1">Back</Button>
              <Button onClick={handleSubmit} className="btn-primary flex-1" disabled={submitting || !selfie} data-testid="kyc-submit">
                {submitting ? 'Submitting...' : 'Submit for Review'}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="card-dark text-center">
            <CheckCircle2 className="h-16 w-16 mx-auto text-[#28A745] mb-4" />
            <h2 className="text-2xl font-black text-white mb-2">Documents Submitted!</h2>
            <p className="text-gray-400 mb-6">Your KYC verification is under review. This typically takes 24-48 hours.</p>
            <div className="bg-[#2D2D2D] rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-[#D4AF37]">
                <AlertCircle className="h-5 w-5" />
                <span className="font-semibold text-sm">Status: Under Review</span>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default KYCPage;
